// src/pages/account/Profile.js
import React, { useEffect, useMemo, useState } from "react";
import { updateProfile } from "firebase/auth";
import {
  doc,
  getDoc,
  updateDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

import { auth, db, storage } from "../../shared/libs/firebaseConfig";
import { useAuth } from "../../shared/auth/AuthProvider"; // ใช้ useAuth จาก AuthProvider

import { useNavigate } from "react-router-dom";

import { Modal, Button } from "react-bootstrap";

export default function Profile() {
  const navigate = useNavigate();
  const { user, profile, refreshUser, logout } = useAuth();

  // ฟอร์มข้อมูลโปรไฟล์ (สอดคล้องกับสคีมาที่แนะนำ)
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [website, setWebsite] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [twitter, setTwitter] = useState("");
  const [facebook, setFacebook] = useState("");
  const [linkedin, setLinkedin] = useState("");

  const [instagram, setInstagram] = useState("");
  const [youtube, setYoutube] = useState("");
  const [tiktok, setTiktok] = useState("");

  // รูปโปรไฟล์
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");
  const [savingAvatar, setSavingAvatar] = useState(false);

  const [savingProfile, setSavingProfile] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(true);

  // Modal แจ้งเตือน
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");

  const [enrollFullName, setEnrollFullName] = useState("");
  const [enrollPhoneNumber, setEnrollPhoneNumber] = useState("");
  const [enrollShippingAddress, setEnrollShippingAddress] = useState("");
  const [savingEnrollProfile, setSavingEnrollProfile] = useState(false);

  const uid = user?.uid || null;

  useEffect(() => {
    if (!uid) return;
      setLoadingDoc(true);

      const data = profile || {};

      const dName = data.displayName ?? user?.displayName ?? "";
      setDisplayName(dName);
      setBio(data.bio ?? "");
      setWebsite(data.website ?? "");
      setContactEmail(data.contactEmail ?? "");
      setTwitter(data.twitter ?? "");
      setFacebook(data.facebook ?? "");
      setLinkedin(data.linkedin ?? "");

      setInstagram(data.instagram ?? "");
      setYoutube(data.youtube ?? "");
      setTiktok(data.tiktok ?? "");

      const url = data.photoURL || user?.photoURL || "";
      setAvatarPreview(url);

      setLoadingDoc(false);

      const enroll = data.enrollProfile || {};

      setEnrollFullName(
        enroll.fullName ?? data.displayName ?? user?.displayName ?? ""
      );

      setEnrollPhoneNumber(
        enroll.phoneNumber ?? data.phoneNumber ?? user?.phoneNumber ?? ""
      );

      setEnrollShippingAddress(
        enroll.shippingAddress ?? data.shippingAddress ?? ""
      );

      return () => {
        if (avatarPreview?.startsWith("blob:")) {
          URL.revokeObjectURL(avatarPreview);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, profile, user]);




  // ปุ่มเลือกไฟล์
  const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
  const MAX_AVATAR_SIZE = 5 * 1024 * 1024; // 5MB

  const onPickAvatar = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    if (!ALLOWED_IMAGE_TYPES.includes(f.type)) {
      setAvatarFile(null);
      e.target.value = "";

      setModalTitle("ไฟล์ไม่ถูกต้อง");
      setModalMessage("กรุณาอัปโหลดเฉพาะไฟล์รูปภาพ JPG, PNG หรือ WEBP เท่านั้น");
      setShowModal(true);
      return;
    }

    if (f.size > MAX_AVATAR_SIZE) {
      setAvatarFile(null);
      e.target.value = "";

      setModalTitle("ไฟล์ใหญ่เกินไป");
      setModalMessage("กรุณาอัปโหลดรูปภาพขนาดไม่เกิน 5MB");
      setShowModal(true);
      return;
    }

    setAvatarFile(f);

    const blobUrl = URL.createObjectURL(f);
    setAvatarPreview(blobUrl);
  };

  // ลบไฟล์รูปเดิมใน Storage (ถ้ามี)
  const deleteOldAvatarIfAny = async (currentAvatarPath) => {
    if (!currentAvatarPath) return;
    try {
      await deleteObject(ref(storage, currentAvatarPath));
    } catch {
      // เงียบได้ ไฟล์อาจถูกลบไปแล้ว
    }
  };

  // อัปโหลดและแทนที่รูปโปรไฟล์ตามเวิร์กโฟลว์
  const saveAvatar = async () => {
    if (!uid || !avatarFile) return;
    setSavingAvatar(true);
    try {
      // ดึง user doc ล่าสุดเพื่ออ่าน avatarPath ปัจจุบัน
      const userRef = doc(db, "users", uid);
      const snap = await getDoc(userRef);
      const curr = snap.exists() ? snap.data() : {};

      // 1) ลบไฟล์เดิมถ้ามี
      await deleteOldAvatarIfAny(curr.avatarPath);

      // 2) อัปโหลดไฟล์ใหม่ด้วยชื่อใหม่ (กันแคช/จัดระเบียบ)
      const ext = (avatarFile.name?.split(".").pop() || "jpg").toLowerCase();
      const newPath = `users/${uid}/avatar_${Date.now()}.${ext}`;
      const storageRef = ref(storage, newPath);
      await uploadBytes(storageRef, avatarFile);

      // 3) getDownloadURL และอัปเดต Firestore + Auth
      const url = await getDownloadURL(storageRef);

      await updateDoc(userRef, {
        photoURL: url,
        avatarPath: newPath,
        updatedAt: serverTimestamp(),
      });

      // sync ไป publicProfiles ด้วย
      const publicRef = doc(db, "publicProfiles", uid);
      await setDoc(
      publicRef,
      {
      uid,
      photoURL: url,
      updatedAt: serverTimestamp(),
      },
      { merge: true }
      );

      // อัปเดต Auth ด้วย (ให้ Header/เมนูเห็นรูปใหม่ทันที)
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, { photoURL: url });
        await refreshUser?.();
      }

      // alert("อัปเดตรูปโปรไฟล์เรียบร้อย");
       setModalTitle("บันทึกรูปโปรไฟล์");
       setModalMessage("บันทึกรูปโปรไฟล์เรียบร้อยแล้ว");
       setShowModal(true);
    } catch (e) {
      console.error(e);
      // alert("บันทึกรูปไม่สำเร็จ");
      setModalTitle("เกิดข้อผิดพลาด");
      setModalMessage("ไม่สามารถบันทึกรูปโปรไฟล์ได้ กรุณาลองใหม่อีกครั้ง");
      setShowModal(true);
    } finally {
      setSavingAvatar(false);
    }
  };

  // ลบรูปโปรไฟล์ (ตั้งค่าเป็นค่าว่าง)
  const removeAvatar = async () => {
    if (!uid) return;
    setSavingAvatar(true);
    try {
      const userRef = doc(db, "users", uid);
      const snap = await getDoc(userRef);
      const curr = snap.exists() ? snap.data() : {};

      // ลบไฟล์เดิมถ้ามี
      await deleteOldAvatarIfAny(curr.avatarPath);

      // เคลียร์ค่าใน Firestore + Auth
      await updateDoc(userRef, {
        photoURL: "",
        avatarPath: "",
        updatedAt: serverTimestamp(),
      });

      
      const publicRef = doc(db, "publicProfiles", uid);
        await setDoc(
          publicRef,
          {
            uid,
            photoURL: "",
            updatedAt: serverTimestamp(),
          },
        { merge: true }
      );

      if (auth.currentUser) {
        await updateProfile(auth.currentUser, { photoURL: "" });
        await refreshUser?.();
      }

      setAvatarPreview("");
      setAvatarFile(null);
      // alert("ลบรูปโปรไฟล์เรียบร้อย");
       setModalTitle("ลบรูปโปรไฟล์");
       setModalMessage("ลบรูปโปรไฟล์เรียบร้อยแล้ว");
       setShowModal(true);
    } catch (e) {
      console.error(e);
      // alert("ลบรูปไม่สำเร็จ");
      setModalTitle("เกิดข้อผิดพลาด");
      setModalMessage("ไม่สามารถลบรูปโปรไฟล์ได้ กรุณาลองใหม่อีกครั้ง");
      setShowModal(true);
    } finally {
      setSavingAvatar(false);
    }
  };

  const saveEnrollProfile = async () => {
    if (!uid) return;

    setSavingEnrollProfile(true);

    try {
      const fullNameValue = enrollFullName?.trim() ?? "";
      const phoneValue = enrollPhoneNumber?.trim() ?? "";
      const addressValue = enrollShippingAddress?.trim() ?? "";

      const userRef = doc(db, "users", uid);

      await updateDoc(userRef, {
        enrollProfile: {
          fullName: fullNameValue,
          phoneNumber: phoneValue,
          shippingAddress: addressValue,
          updatedAt: serverTimestamp(),
        },

        // optional: sync ไว้ระดับบนเพื่อให้หน้าอื่นเรียกง่าย
        phoneNumber: phoneValue,
        shippingAddress: addressValue,

        updatedAt: serverTimestamp(),
      });

      setModalTitle("บันทึกโปรไฟล์สมัครเรียน");
      setModalMessage("บันทึกโปรไฟล์สมัครเรียนเรียบร้อยแล้ว");
      setShowModal(true);
    } catch (e) {
      console.error(e);
      setModalTitle("เกิดข้อผิดพลาด");
      setModalMessage("ไม่สามารถบันทึกโปรไฟล์สมัครเรียนได้ กรุณาลองใหม่อีกครั้ง");
      setShowModal(true);
    } finally {
      setSavingEnrollProfile(false);
    }
  };

  // บันทึกข้อมูลโปรไฟล์
  const saveProfile = async () => {
    if (!uid) return;
    setSavingProfile(true);
    try {
      const userRef = doc(db, "users", uid);
      await updateDoc(userRef, {
        displayName: displayName?.trim() ?? "",
        bio: bio ?? "",
        website: website ?? "",
        contactEmail: contactEmail?.trim() ?? "",
        twitter: twitter ?? "",
        facebook: facebook ?? "",
        linkedin: linkedin ?? "",

        instagram: instagram ?? "",
        youtube: youtube ?? "",
        tiktok: tiktok ?? "",

        updatedAt: serverTimestamp(),
      });

      // --- sync โปรไฟล์แบบ public ---
      const publicRef = doc(db, "publicProfiles", uid);
      await setDoc(
        publicRef,
        {
          uid,
          displayName: displayName?.trim() ?? "",
          bioPublic: bio ?? "",
          website: website ?? "",
          twitter: twitter ?? "",
          facebook: facebook ?? "",
          linkedin: linkedin ?? "",

          instagram: instagram ?? "",
          youtube: youtube ?? "",
          tiktok: tiktok ?? "",

          // ใช้รูปจาก Auth เป็นหลัก (เพราะอัปเดตตอน saveAvatar แล้ว)
          photoURL: auth.currentUser?.photoURL ?? "",
          publicProfileStatus: true,        // เปิดเป็น public ไว้ก่อน
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Sync ชื่อไป Auth ด้วย (ถ้าต้องการให้ส่วนหัวแสดงชื่อใหม่ทันที)
      if (auth.currentUser && displayName) {
        await updateProfile(auth.currentUser, { displayName });
        await refreshUser?.();
      }

      // alert("บันทึกโปรไฟล์เรียบร้อย");
       setModalTitle("บันทึกโปรไฟล์");
       setModalMessage("บันทึกโปรไฟล์เรียบร้อยแล้ว");
       setShowModal(true);
    } catch (e) {
      console.error(e);
      // alert("บันทึกโปรไฟล์ไม่สำเร็จ");
      setModalTitle("เกิดข้อผิดพลาด");
      setModalMessage("ไม่สามารถบันทึกข้อมูลโปรไฟล์ได้ กรุณาลองใหม่อีกครั้ง");
      setShowModal(true);
    } finally {
      setSavingProfile(false);
    }
  };

  const maskedEmail = useMemo(() => {
    const em = profile?.email || user?.email || "";
    const [name, dom] = em.split("@");
    if (!name || !dom) return em;
    return name.length <= 3
      ? `${name[0]}***@${dom}`
      : `${name.slice(0, 3)}***@${dom}`;
  }, [profile, user]);

  const isPhoneProvider =
    profile?.primaryProvider === "phone" ||
    profile?.provider === "phone" ||
    profile?.providers?.includes("phone");

  if (!uid) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">กรุณาเข้าสู่ระบบก่อน</div>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: 900 }}>
      <h3 className="mt-4 mb-3">โปรไฟล์ของฉัน</h3>

      {/* สถานะอีเมล */}
    {/*   <div className="alert alert-light d-flex justify-content-between align-items-center">
        <div>
          <div><strong>Email:</strong> {profile?.email || user?.email}</div>
          <div>
            <strong>ยืนยันอีเมล:</strong>{" "}
            {user?.emailVerified ? (
              <span className="text-success">ยืนยันแล้ว</span>
            ) : (
              <span className="text-danger">ยังไม่ยืนยัน</span>
            )}
          </div>
        </div>
      </div> */}
      {/* สถานะอีเมล: แสดงเฉพาะบัญชีที่ไม่ได้เข้าสู่ระบบด้วย phone auth */}
      {!isPhoneProvider && (profile?.email || user?.email) ? (
        <div className="alert alert-light d-flex justify-content-between align-items-center">
          <div>
            <div>
              <strong>Email:</strong> {profile?.email || user?.email}
            </div>
          </div>
        </div>
      ) : null}

      {/* Avatar */}
      <div className="card mb-4">
        <div className="card-body d-flex flex-column flex-sm-row align-items-center">
          <div className="flex-shrink-0 mb-3 mb-sm-0" style={{ width: 96, height: 96, borderRadius: "50%", overflow: "hidden", background: "#f2f2f2" }}>
            {avatarPreview ? (
              <img
                src={avatarPreview}
                alt="avatar"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted">
                ไม่มีรูป
              </div>
            )}
          </div>

          <div className="ms-3">
            <input
              className="form-control mb-2"
              type="file"
              accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
              onChange={onPickAvatar}
            />
            <div className="d-flex gap-2">
              <button
                className="btn btn-primary"
                onClick={saveAvatar}
                disabled={!avatarFile || savingAvatar}
              >
                {savingAvatar ? "กำลังบันทึก…" : "บันทึกรูปโปรไฟล์"}
              </button>
              <button
                className="btn btn-outline-danger"
                onClick={removeAvatar}
                disabled={savingAvatar}
              >
                ลบรูปโปรไฟล์
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Enroll profile form */}
      <div className="card mb-4 border-info">
        <div className="card-body">
          <h4 className="h5 mb-3">โปรไฟล์สมัครเรียน</h4>

          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">ชื่อ-นามสกุล</label>
              <input
                className="form-control"
                value={enrollFullName}
                onChange={(e) => setEnrollFullName(e.target.value)}
                disabled={loadingDoc}
                placeholder="ชื่อ-นามสกุล สำหรับสมัครเรียน"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">เบอร์โทรศัพท์</label>
              <input
                className="form-control"
                value={enrollPhoneNumber}
                onChange={(e) => setEnrollPhoneNumber(e.target.value)}
                disabled={loadingDoc}
                placeholder="เช่น 0812345678"
              />
            </div>

            <div className="col-12">
              <label className="form-label">ที่อยู่รับเอกสาร</label>
              <textarea
                className="form-control"
                rows={3}
                value={enrollShippingAddress}
                onChange={(e) => setEnrollShippingAddress(e.target.value)}
                disabled={loadingDoc}
                placeholder="ที่อยู่สำหรับจัดส่งเอกสารคอร์ส"
              />
            </div>

            <div className="col-12">
              <div className="small text-muted">
                หากใช้ที่อยู่เดิมในการสมัครไปแล้ว การเปลี่ยนแปลงข้อมูลส่วนนี้จะไม่มีผลกับข้อมูลการสมัครที่ได้สมัครไปแล้ว
              </div>
            </div>
          </div>

          <div className="mt-3">
            <button
              className="btn btn-primary"
              onClick={saveEnrollProfile}
              disabled={savingEnrollProfile || loadingDoc}
            >
              {savingEnrollProfile ? "กำลังบันทึก…" : "บันทึกโปรไฟล์สมัครเรียน"}
            </button>
          </div>
        </div>
      </div>

      {/* Profile form */}
      <div className="card mb-5">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">ชื่อที่แสดง</label>
              <input
                className="form-control"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={loadingDoc}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">เว็บไซต์</label>
              <input
                className="form-control"
                placeholder="https://chicchiclawtutor.com"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                disabled={loadingDoc}
              />
            </div>

            <div className="col-md-12">
              <label className="form-label">อีเมลสำหรับติดต่อ</label>
              <input
                className="form-control"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                disabled={loadingDoc}
                placeholder="example@email.com"
              />
              <div className="small text-muted mt-1">
                อีเมลนี้ใช้สำหรับการติดต่อจากทีมงานเท่านั้น
                หากบัญชีนี้เข้าสู่ระบบด้วยหมายเลขโทรศัพท์ จะยังไม่สามารถใช้อีเมลนี้เข้าสู่ระบบแทนเบอร์โทรศัพท์ได้
              </div>
            </div>

            <div className="col-12">
              <label className="form-label">แนะนำตัว (bio)</label>
              <textarea
                className="form-control"
                rows={3}
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                disabled={loadingDoc}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Twitter (X)</label>
              <input
                className="form-control"
                placeholder="@username"
                value={twitter}
                onChange={(e) => setTwitter(e.target.value)}
                disabled={loadingDoc}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Facebook</label>
              <input
                className="form-control"
                placeholder="โปรไฟล์/เพจ"
                value={facebook}
                onChange={(e) => setFacebook(e.target.value)}
                disabled={loadingDoc}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">LinkedIn</label>
              <input
                className="form-control"
                placeholder="โปรไฟล์"
                value={linkedin}
                onChange={(e) => setLinkedin(e.target.value)}
                disabled={loadingDoc}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Instagram</label>
              <input
                className="form-control"
                placeholder="โปรไฟล์/เพจ"
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
                disabled={loadingDoc}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Youtube</label>
              <input
                className="form-control"
                placeholder="โปรไฟล์/เพจ"
                value={youtube}
                onChange={(e) => setYoutube(e.target.value)}
                disabled={loadingDoc}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Tiktok</label>
              <input
                className="form-control"
                placeholder="โปรไฟล์/เพจ"
                value={tiktok}
                onChange={(e) => setTiktok(e.target.value)}
                disabled={loadingDoc}
              />
            </div>


          </div>

          <div className="mt-3 d-flex gap-2">
            <button
              className="btn btn-success"
              onClick={saveProfile}
              disabled={savingProfile || loadingDoc}
            >
              {savingProfile ? "กำลังบันทึก…" : "บันทึกโปรไฟล์"}
            </button>
          </div>
        </div>
      </div>

      {/* ลิงก์ออกจากระบบ มุมขวาล่าง */}
          <div className="mb-4 d-flex justify-content-center">
            <button
              type="button"
              className="btn btn-link text-danger p-0"
              style={{ textDecoration: "none" }}
              onClick={async () => {
                try {
                  await logout();
                  navigate("/");
                } catch (e) {
                  console.error(e);
                }
              }}
            >
              ออกจากระบบ
            </button>
          </div>

          {/* Modal แจ้งเตือนผลการบันทึก */}
          <Modal
            show={showModal}
            onHide={() => setShowModal(false)}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>{modalTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body>{modalMessage}</Modal.Body>
            <Modal.Footer>
              <Button variant="primary" onClick={() => setShowModal(false)}>
                ปิด
              </Button>
            </Modal.Footer>
          </Modal>

  
      
    </div>
  );
}


